import { Capacitor } from "@capacitor/core";
import { PLATFORMS, type Platform } from "@/constants/platforms";

/**
 * Platform domain patterns for URL detection.
 * Uses exact domain matching to avoid false positives (e.g., fbsbx.com matching x.com)
 */
const PLATFORM_DOMAINS: Partial<Record<Platform, string[]>> = {
  [PLATFORMS.INSTAGRAM]: ["instagram.com", "instagr.am", "cdninstagram.com", "fbsbx.com"],
  [PLATFORMS.TIKTOK]: ["tiktok.com"],
  [PLATFORMS.TWITTER]: ["twitter.com", "x.com", "t.co"],
  [PLATFORMS.YOUTUBE]: ["youtube.com", "youtu.be"],
  [PLATFORMS.REDDIT]: ["reddit.com", "redd.it"],
  [PLATFORMS.FACEBOOK]: ["facebook.com", "fb.com", "fb.watch", "fbcdn.net"],
  [PLATFORMS.LINKEDIN]: ["linkedin.com"],
  [PLATFORMS.PINTEREST]: ["pinterest.com", "pin.it"],
  [PLATFORMS.SUBSTACK]: ["substack.com"],
  // WEB has no specific domains - it's detected via source field or as fallback
};

/**
 * Check if a hostname matches a domain pattern.
 * Matches exact domain or subdomains (e.g., "www.instagram.com" matches "instagram.com")
 */
function matchesDomain(hostname: string, domain: string): boolean {
  const sourceHost = domain === "substack.com" ? hostname.replace(/\.$/, "") : hostname;
  return sourceHost === domain || sourceHost.endsWith(`.${domain}`);
}

/**
 * Extract platform name from a canonical URL
 * @param canonicalUrl - The canonical URL from the post
 * @returns Platform name (instagram, tiktok, twitter, etc.) or null
 */
export function getPlatformFromUrl(canonicalUrl?: string | null): Platform | null {
  if (!canonicalUrl || typeof canonicalUrl !== "string") {
    return null;
  }

  let hostname: string;
  try {
    hostname = new URL(canonicalUrl).hostname.toLowerCase();
  } catch {
    return null;
  }

  for (const [platform, domains] of Object.entries(PLATFORM_DOMAINS)) {
    if (domains.some((domain) => matchesDomain(hostname, domain))) {
      return platform as Platform;
    }
  }

  return null;
}

/**
 * Detect platform from a source string (e.g., "instagram", "tiktok")
 */
export function getPlatformFromString(value?: string | null): Platform | null {
  if (!value || typeof value !== "string") return null;

  const normalized = value.toLowerCase();

  if (["substack", "substack.com"].includes(normalized.trim())) return PLATFORMS.SUBSTACK;
  if (normalized.includes("instagram")) return PLATFORMS.INSTAGRAM;
  if (normalized.includes("tiktok")) return PLATFORMS.TIKTOK;
  if (normalized.includes("twitter") || normalized === "x" || normalized.includes("x.com")) return PLATFORMS.TWITTER;
  if (normalized.includes("youtube") || normalized.includes("youtu.be")) return PLATFORMS.YOUTUBE;
  if (normalized.includes("facebook") || normalized.includes("fb.com")) return PLATFORMS.FACEBOOK;
  if (normalized.includes("reddit")) return PLATFORMS.REDDIT;
  if (normalized.includes("linkedin")) return PLATFORMS.LINKEDIN;
  if (normalized.includes("pinterest")) return PLATFORMS.PINTEREST;

  return null;
}

/**
 * Post-like object for platform detection
 */
interface PostLike {
  sourceUrl?: string;
  canonical_url?: string | null;
  original_url?: string | null;
  content?: { url?: string } | Record<string, unknown>;
  source?: string | { platform?: string; username?: string; url?: string };
  platform?: string;
}

/**
 * Get platform from a post object
 * Tries URL first, then falls back to source/platform fields
 */
export function getPlatformFromPost(post: PostLike): Platform | null {
  // Try to get platform from URL first (prefer content.url)
  const contentUrl = (post.content as Record<string, unknown>)?.url;
  const sourceUrl = typeof contentUrl === "string"
    ? contentUrl
    : post.sourceUrl || (typeof post.source === "object" ? post.source?.url : undefined);
  if (sourceUrl) {
    const platformFromUrl = getPlatformFromUrl(sourceUrl);
    if (platformFromUrl) return platformFromUrl;
  }

  // A publication may use a custom outbound domain while its other recorded
  // source URL identifies Substack. Recognition does not replace that link.
  const sourceUrls = [post.sourceUrl, post.canonical_url, post.original_url,
    typeof post.source === "object" ? post.source?.url : undefined];
  if (sourceUrls.some(url => getPlatformFromUrl(url) === PLATFORMS.SUBSTACK)) {
    return PLATFORMS.SUBSTACK;
  }

  // Fallback to source field
  const source = typeof post.source === "string" ? post.source : post.source?.platform;
  if (typeof source === "string") {
    const platformFromSource = getPlatformFromString(source);
    if (platformFromSource) return platformFromSource;
  }

  // Fallback to platform field
  const platform = post.platform;
  if (platform) {
    const platformFromField = getPlatformFromString(platform);
    if (platformFromField) return platformFromField;
  }

  return null;
}

const PROFILE_URL_TEMPLATES: Partial<Record<Platform, string>> = {
  [PLATFORMS.INSTAGRAM]: "https://www.instagram.com/{username}",
  [PLATFORMS.TIKTOK]: "https://www.tiktok.com/@{username}",
  [PLATFORMS.YOUTUBE]: "https://www.youtube.com/@{username}",
  [PLATFORMS.TWITTER]: "https://x.com/{username}",
  [PLATFORMS.FACEBOOK]: "https://www.facebook.com/{username}",
  [PLATFORMS.REDDIT]: "https://www.reddit.com/user/{username}",
  [PLATFORMS.LINKEDIN]: "https://www.linkedin.com/in/{username}",
  [PLATFORMS.PINTEREST]: "https://www.pinterest.com/{username}",
};

/**
 * Build a profile URL for a username on a given platform.
 * Returns null when the platform is unknown or has no template.
 */
export function getProfileUrl(platform: Platform | null, username: string): string | null {
  if (!platform) return null;
  const template = PROFILE_URL_TEMPLATES[platform];
  if (!template) return null;
  return template.replace("{username}", username);
}

/**
 * Get display name for a platform
 * @param platform - The platform constant
 * @returns Human-readable platform name
 */
export function getPlatformDisplayName(platform: Platform | null): string {
  if (!platform) return "Web";

  switch (platform) {
    case PLATFORMS.INSTAGRAM:
      return "Instagram";
    case PLATFORMS.TIKTOK:
      return "TikTok";
    case PLATFORMS.TWITTER:
      return "X.com";
    case PLATFORMS.YOUTUBE:
      return "YouTube";
    case PLATFORMS.FACEBOOK:
      return "Facebook";
    case PLATFORMS.REDDIT:
      return "Reddit";
    case PLATFORMS.LINKEDIN:
      return "LinkedIn";
    case PLATFORMS.PINTEREST:
      return "Pinterest";
    case PLATFORMS.SUBSTACK:
      return "Substack";
    default:
      return "Web";
  }
}

/** The Instagram post used as the onboarding welcome guide (native — app share tutorial). */
export const WELCOME_GUIDE_URL = "https://www.instagram.com/p/DVO7PAClPi3/";
/** The Instagram post used as the onboarding welcome guide (web — web share tutorial). */
export const WELCOME_GUIDE_URL_WEB = "https://www.instagram.com/p/DVR-HCBlG3b/";

type AppLaunch = { probeScheme: string; launchUrl: string };

/**
 * Per-platform native launch resolver.
 *
 * `probeScheme` is what we hand to `canOpenUrl` to detect the app; `launchUrl`
 * is what we actually launch once the app is confirmed present. They differ
 * because some apps (TikTok) hijack https URLs into their in-app browser, so
 * we must launch the bare scheme — while others (Instagram) honor Universal
 * Links and route the https URL to the right post natively.
 */
const PLATFORM_LAUNCHERS: Record<string, (webUrl: string) => AppLaunch> = {
  instagram: (webUrl) => ({ probeScheme: "instagram://", launchUrl: webUrl }),
  tiktok: () => ({ probeScheme: "tiktok://", launchUrl: "tiktok://" }),
  facebook: () => ({ probeScheme: "fb://", launchUrl: "fb://" }),
  x: () => ({ probeScheme: "twitter://", launchUrl: "twitter://" }),
  reddit: () => ({ probeScheme: "reddit://", launchUrl: "reddit://" }),
};

/**
 * Open a URL, preferring the native app when available.
 * Falls back to in-app browser (native) or new tab (web).
 */
export async function openExternalUrl(url: string, appLaunch?: AppLaunch | null) {
  if (Capacitor.isNativePlatform()) {
    if (appLaunch) {
      const { AppLauncher } = await import("@capacitor/app-launcher");
      const { value: canOpen } = await AppLauncher.canOpenUrl({ url: appLaunch.probeScheme });
      if (canOpen) {
        await AppLauncher.openUrl({ url: appLaunch.launchUrl });
        return;
      }
    }
    const { Browser } = await import("@capacitor/browser");
    await Browser.open({ url });
  } else {
    window.open(url, "_blank", "noopener,noreferrer");
  }
}

/**
 * Open a social platform URL, automatically resolving the native launch target.
 * The launcher is looked up by platform ID (e.g. "instagram", "tiktok").
 */
export async function openSocialUrl(url: string, platformId?: string) {
  const resolver = platformId ? PLATFORM_LAUNCHERS[platformId] : null;
  await openExternalUrl(url, resolver ? resolver(url) : null);
}
